import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useContext
} from 'react'
import {
  usePersistentState,
  useBrowserLocation,
  useDraggable,
  parseARGB
} from '../../../hooks'
import {
  GoogleMap,
  StandaloneSearchBox,
  Marker,
  InfoWindow,
  TrafficLayer,
  Polyline
} from '@react-google-maps/api'
import '@fortawesome/fontawesome-free/css/all.min.css'
import { getDownloadURL, ref } from 'firebase/storage'
import { CloseOutlined, PlusOutlined, FileOutlined, PlayCircleOutlined } from '@ant-design/icons'
import { Descriptions, Image, Upload } from 'antd'

import { fetchUserProfiles } from '../../../hooks/userProfiles'
import IncidentDetails from '../../IncidentDetails'
import UserDetails from '../../UserDetails'
import ModalDialog from '../../ModalDialog'
import TextField from '../../Form/TextField'
import TextAreaField from '../../Form/TextAreaField'
import ColorField from '../../Form/ColorField'
import Button from '../../Button'

import styles from './styles'
import './styles.scss'
import { iconToUnicode } from './icons'
import { useApi, useError, useForm } from '../../../hooks'
import {
  createIncidentMapMarker,
  deleteIncidentMapMarker,
  updateIncidentMapMarker,
  uploadAttachments,
  deleteAttachmentFromStorage
} from '../../../hooks/firebaseService'
import { syncPinAttachments } from '../../../api'
import toast from 'react-hot-toast'
import { ApiContext, usePermissions, useRest } from '../../../hooks/api'
import TrackingSidebar from './TrackingSidebar'
import { TrackingPinModal } from './TrackingPinModal'
import { TrackService } from '../../../services/TrackService'
import CustomPinInfoWindow from './CustomPinInfoWindow'
import TrackInfoWindow from './TrackInfoWindow'
import TrackMarkers from './TrackMarkers'
import { Edit, MapPin, MapPinIcon, Pin, PinIcon, Trash } from 'lucide-react'
import { renderToStaticMarkup } from 'react-dom/server'
import {
  addMessageToChat,
  createPinMessage,
  updatePinMessageInChat,
  deletePinMessageFromChat
} from '../../../services/chatService'
import { GPSWanderingDetector } from '../../../hooks/gpsSmoother'
import { auth } from '../../../firebase'
import create from '@ant-design/icons/lib/components/IconFont'
import { useHeicConverter } from '../../../hooks/useHeicConverter'
import Spinner from '../../Spinner'
import { PinAttachmentPreview } from './PinAttachmentPreview'
import DeleteConfirmationModal from '../../DeleteConfirmationModal'
import { useMap } from '../../../hooks/MapContext'

const SEARCHBOX_BOUNDS_RADIUS_METERS = 50000

function SmoothedLivePolyline({ path, onClick }) {
  // ← HOOK FIRST — NO EARLY RETURN BEFORE THIS
  const smoothedPath = React.useMemo(() => {
    if (!path || path.length < 2) {
      return []
    }

    const gpsCleaner = new GPSWanderingDetector()
    const cleaned = gpsCleaner.removeWanderingPoints({ coordinates: path })
    return cleaned
  }, [path])

  // ← Now safe to return early
  if (smoothedPath.length < 2) {
    return null
  }

  return (
    <Polyline
      path={smoothedPath}
      options={{
        strokeColor: '#005ad0',
        strokeOpacity: 0.8,
        strokeWeight: 6,
        clickable: true,
        draggable: false,
        editable: false,
        visible: true,
        zIndex: 1
      }}
      onClick={onClick}
    />
  )
}

function calculateDistanceInMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const distance = R * c
  return distance
}

function TrackPolyline({ track, onClick, isSelected }) {
  // ← HOOK MUST BE FIRST — NO RETURN BEFORE IT
  const path = React.useMemo(() => {
    if (
      !track.points ||
      !Array.isArray(track.points) ||
      track.points.length === 0
    ) {
      return []
    }

    const gpsCleaner = new GPSWanderingDetector()
    const coordinates = track.points.map((point) => ({
      lat: point.latitude,
      lng: point.longitude
    }))
    const cleaned = gpsCleaner.removeWanderingPoints({ coordinates })
    return cleaned
  }, [track.points])

  // ← Now safe to return early
  if (path.length === 0) {
    return null
  }



  const parsed = parseARGB(track.color_code);

  return (
    <Polyline
      path={path}
      options={{
        strokeColor: track.color_code
          ? parsed?.color || track.color_code
          : isSelected
            ? '#ff6b00'
            : '#3366ff',
        strokeOpacity: isSelected ? 0.9 : 0.6,
        strokeWeight: isSelected ? 6 : 4,
        clickable: true,
        draggable: false,
        editable: false,
        visible: true,
        zIndex: isSelected ? 2 : 1
      }}
      onClick={() => onClick(track)}
    />
  )
}

function CustomPin({ pin, map, onClick }) {
  const { position } = pin

  const svgString = renderToStaticMarkup(
    <MapPin color={pin?.color || '#eb144c'} size={32} />
  )
  const dataUrl = `data:image/svg+xml;base64,${btoa(svgString)}`

  return (
    <GoogleMapsMarker
      position={position}
      icon={{
        url: dataUrl,
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 32)
      }}
      zIndex={1001}
      onClick={(marker) => onClick && onClick(marker, pin)}
    />
  )
}

function convertLocation(loc) {
  return { lng: loc[0], lat: loc[1] }
}

export class MapSelection {
  constructor(loc, placeId) {
    this.loc = loc
    this.placeId = placeId
  }
}

export function useMapSettings() {
  return usePersistentState('dispatch-map-settings', {
    showTrafficLayer: false,
    defaultMapCenter: undefined,
    defaultMapZoom: undefined,
    showLastName: false,
    defaultMapType: 'satellite'
  })
}

function GoogleMapsMarker({ onClick, ...props }) {
  const [marker, setMarker] = useState()
  return (
    <Marker
      onLoad={setMarker}
      onUnmount={() => setMarker(null)}
      onClick={() => onClick && onClick(marker)}
      {...props}
    />
  )
}

function MarkerWithFaIcon({ icon, color, ...props }) {
  return (
    <GoogleMapsMarker
      label={{
        text: iconToUnicode(icon),
        color: 'white',
        fontWeight: '600',
        fontSize: '16px',
        fontFamily: "'Font Awesome 5 Free'",
        className: 'dispatch-incident-marker-label'
      }}
      {...props}
    />
  )
}

function createCanvas(size) {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size

  const ctx = canvas.getContext('2d')
  return [canvas, ctx]
}

function createTriangleMarker(size, color) {
  const [canvas, ctx] = createCanvas(size)

  ctx.beginPath()
  ctx.moveTo(0, 1)
  ctx.lineTo(size * 0.5, size)
  ctx.lineTo(size, 1)
  ctx.lineTo(0, 1)
  ctx.fillStyle = color
  ctx.fill()
  ctx.lineWidth = '1px'
  ctx.strokeStyle = '#333333'
  ctx.stroke()

  return canvas.toDataURL()
}

function createFaIconMarker(size, color, icon) {
  const [canvas, ctx] = createCanvas(size)

  ctx.font = `900 ${size}px 'Font Awesome 5 Free'`
  ctx.fillStyle = color

  const iconSize = ctx.measureText(icon)
  ctx.fillText(
    icon,
    size * 0.5 - iconSize.width * 0.5,

    iconSize.actualBoundingBoxAscent
  )

  return canvas.toDataURL()
}

const DEVICE_MARKER_SIZE = 64
const DEVICE_MARKER_ICON_CACHE = {}

function DeviceMarker({ text, color, ...props }) {
  const iconUrl =
    DEVICE_MARKER_ICON_CACHE[color] ||
    createTriangleMarker(DEVICE_MARKER_SIZE, color)
  DEVICE_MARKER_ICON_CACHE[color] = iconUrl

  return (
    <Marker
      label={{
        text,
        color: 'white',
        fontWeight: '800',
        fontSize: '20px',
        className: 'dispatch-device-marker-label'
      }}
      icon={{
        url: iconUrl,
        scaledSize: { width: 24, height: 24 }
      }}
      {...props}
    />
  )
}

function DeviceMarkersWrapper({ deviceLocations, openSecondaryPanel }) {
  const [{ showLastName }] = useMapSettings()
  const [fetchedUsers, setFetchedUsers] = useState({}) // cache by userId

  useEffect(() => {
    const fetchMissingUsers = async () => {
      const missingUserIds = Object.values(deviceLocations)
        .map((d) => d.user)
        .filter(
          (u) =>
            u &&
            u.id &&
            (!u.displayName && !fetchedUsers[u.id]) // only if incomplete & not fetched
        )
        .map((u) => u.id)

      if (!missingUserIds.length) return

      try {
        // Responders on a shared incident may sit outside our bindings, and the
        // batched read was refused for the whole group when one of them did —
        // every marker on the map lost its name, not just theirs.
        const usersMap = await fetchUserProfiles(missingUserIds)
        setFetchedUsers((prev) => ({ ...prev, ...usersMap }))
      } catch (err) {
        console.error('Failed to fetch user details:', err)
      }
    }

    fetchMissingUsers()
  }, [deviceLocations, fetchedUsers])

  return Object.entries(deviceLocations).map(([deviceId, device]) => {
    let { loc, user } = device
    if (!user) return null

    // 🔁 merge fetched user data if needed
    const fullUser = fetchedUsers[user.id]
      ? { ...user, ...fetchedUsers[user.id] }
      : user

    const { id, userType, displayName, fullname } = fullUser

    let displayNameToShow = displayName || 'User'

    if (showLastName && fullname) {
      displayNameToShow = fullname.split(' ')[1] || displayNameToShow
    }

    return (
      <DeviceMarker
        key={deviceId}
        text={displayNameToShow}
        color={userType?.color || '#ff0000'}
        position={convertLocation(loc)}
        onClick={() =>
          openSecondaryPanel(<UserDetails id={id} />, displayNameToShow)
        }
      />
    )
  })
}

function IncidentMarkersWrapper({
  incidents,
  selectedIncident,
  onClick,
  iconSize
}) {
  return Object.values(incidents)
    .filter(
      (incident) =>
        incident.status === 'inprogress' &&
        (!selectedIncident || selectedIncident.id === incident.id)
    )
    .map((incident) => {
      const loc = incident.locations.find((loc) => loc.format === 'lat-long')
      if (loc) {
        incident.loc = [loc.longitude, loc.latitude]
      }
      return incident
    })
    .filter(({ loc }) => loc)
    .map((incident) => (
      <MarkerWithFaIcon
        iconSize={iconSize}
        key={incident.id}
        position={convertLocation(incident.loc)}
        icon='FaExclamationCircle'
        title={`Incident "${incident.name}"`}
        onClick={(marker) => onClick(marker, incident)}
      />
    ))
}

function IncidentInfoWindow({ incident, anchor, onClose, onOpenDetails }) {
  return (
    <InfoWindow
      anchor={anchor}
      onCloseClick={() => onClose()}
      options={{
        maxWidth: 400,
        autoPanPadding: { top: 180, bottom: 50, left: 50, right: 50 }
      }}>
      <div>
        <h2 className='subtitle' style={{ marginBottom: '0.25rem' }}>
          {incident.name}
        </h2>
        <div style={{ marginBottom: '0.5rem' }}>{incident.description}</div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <a onClick={() => onOpenDetails(incident.id)}>Details</a>
        </div>
      </div>
    </InfoWindow>
  )
}


const mapAttachmentsToSync = (atts) => (Array.isArray(atts) ? atts : []).map(att => {
  const url = att.photo_url || att.url;
  let fileType = att.fileType;

  // Re-detect fileType if missing (common if loaded from incident collection)
  if (!fileType && url) {
    if (/\.(jpg|jpeg|png|gif|webp|heic|heif)(\?.*)?$/i.test(url)) {
      fileType = 'jpg';
    } else if (/\.(mp4|mov|avi|wmv|flv|mkv)(\?.*)?$/i.test(url)) {
      fileType = 'mp4';
    } else {
      fileType = 'file';
    }
  } else if (fileType?.includes('/')) {
    fileType = fileType.split('/')[1];
  }

  return {
    url: url,
    fileType: fileType || 'file',
    fileSize: `${att.fileSize || 0}`,
    thumbnail: att.thumbnail || null
  };
})


function IncidentMapMarkerInfoWindow({
  incident,
  mapMarker,
  anchor,
  onClose,
  onOpenDetails,
  onDeleteMarker, user
}) {
  const updatePin = usePermissions('incident', 'updateAny')
  const deletePin = usePermissions('incident', 'updateAny')
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [existingAttachments, setExistingAttachments] = useState([])

  // form hook
  const [getFieldProps, formData] = useForm({
    dependencies: [isEditing]
  })

  useEffect(() => {
    if (isEditing) {
      setExistingAttachments(mapMarker.attachments || [])
    }
  }, [isEditing, mapMarker.attachments])

  const [fileList, setFileList] = useState([])

  const handleFileChange = ({ fileList }) => setFileList(fileList)

  const handleRemoveExistingAttachment = (id) => {
    setExistingAttachments((prev) => prev.filter((img) => img.id !== id))
  }

  const handleSave = async () => {
    try {
      setLoading(true)

      let newAttachments = []

      if (fileList.length > 0) {
        newAttachments = await uploadAttachments({
          fileList,
          incident,
          pinId: mapMarker.id,
          userId: auth.currentUser.uid
        })
      }

      const finalAttachments = [
        ...existingAttachments,
        ...newAttachments
      ]

      // Update the map marker in Firestore
      await updateIncidentMapMarker(incident.id, mapMarker.id, {
        label: formData?.label || mapMarker.label,
        description: formData?.description || mapMarker.description,
        attachments: finalAttachments.map((att) => ({
          created_at: att.created_at,
          id: att.id,
          photo_url: att.photo_url || att.url,
          pin_id: att.pin_id,
          user_id: att.user_id,
          thumbnail: att.thumbnail
        }))
      });


      // ===== SYNC PIN ATTACHMENTS VIA CLOUD FUNCTION =====
      if (incident?.chatId) {
        try {
          const senderData = {
            id: user?.id || auth.currentUser.uid,
            displayName: user?.displayName || 'User',
            profileUrl: user?.profileUrl || ''
          }

          const pinLocation = {
            latitude: mapMarker.latitude.toString(),
            longitude: mapMarker.longitude.toString()
          }

          await syncPinAttachments({
            incidentId: incident.id,
            markerId: mapMarker.id,
            chatId: incident.chatId,
            oldAttachments: mapAttachmentsToSync(mapMarker.attachments || []),
            newAttachments: mapAttachmentsToSync(finalAttachments),
            senderData,
            pinLocation
          })
        } catch (syncError) {
          console.error('Pin updated but failed to sync attachments:', syncError)
        }
      }



      setIsEditing(false)
      setFileList([])
      toast.success('Pin updated successfully')

    } catch (err) {
      console.error('Update marker failed:', err)
      toast.error('Failed to update marker')
    }

    setLoading(false)
  }

  return (
    <InfoWindow
      key={isEditing ? 'edit' : 'view'}
      anchor={anchor}
      onCloseClick={() => onClose()}
      options={{
        maxWidth: 400,
        pixelOffset: new window.google.maps.Size(0, -40),
        autoPanPadding: { top: 180, bottom: 50, left: 50, right: 50 }
      }}>
      <div className='w-full max-w-[400px] bg-white rounded-lg shadow-lg'>
        {/* Header */}
        <div className='text-lg font-semibold text-gray-900 pb-2 border-b'>
          Pin Info
        </div>
        <div className='py-4 px-2 '>
          <div className='flex items-center justify-between mb-2 gap-1'>
            {!isEditing ? (
              <h3 className='text-lg font-semibold text-gray-800 mb-0'>
                Name: {mapMarker.label}
              </h3>
            ) : (
              <TextField
                label='Label'
                name='label'
                defaultValue={mapMarker.label}
                maxLength={10}
                required={true}
                {...getFieldProps()}
              />
            )}

            <div className='flex gap-2'>
              {updatePin && updatePin.granted && !isEditing && (
                <Edit className='w-5 h-5 cursor-pointer' onClick={() => setIsEditing(true)} />
              )}

              {deletePin && deletePin.granted && (
                <Trash
                  className='w-5 h-5 cursor-pointer'
                  onClick={() => onDeleteMarker(mapMarker, incident)}
                />
              )}
            </div>
          </div>

          {/* Content */}
          <div className='space-y-2 mb-4'>
            {!isEditing ? (
              <p className='text-gray-600 text-sm'>
                Descriptions: {mapMarker.description}
              </p>
            ) : (
              <TextAreaField
                label='Description'
                name='description'
                defaultValue={mapMarker.description}
                placeholder='Description'
                rows={2}
                style={{ resize: "none" }}
                {...getFieldProps()}
              />
            )}

            <div className='flex items-center text-sm'>
              <span className='font-medium text-gray-700'>Incident:</span>
              <span className='ml-1 text-gray-600'>
                {incident?.name || incident?.description}
              </span>
            </div>

            {/* ===== VIEW MODE ATTACHMENTS ===== */}
            {!isEditing && mapMarker.attachments?.length > 0 && (
              <Image.PreviewGroup>
                <div className='grid grid-cols-2 gap-2 mt-2'>
                  {mapMarker.attachments.map((att) => (
                    <PinAttachmentPreview
                      key={att.id}
                      attachment={att}
                    />
                  ))}
                </div>
              </Image.PreviewGroup>
            )}


            {/* ===== EDIT MODE ATTACHMENTS ===== */}
            {isEditing && (
              <>
                <label className='text-sm font-medium'>Attachments</label>

                {/* Existing attachments with remove */}
                {existingAttachments.length > 0 && (
                  <div className='grid grid-cols-3 gap-2 mt-2'>
                    {existingAttachments.map((att) => (
                      <PinAttachmentPreview
                        key={att.id}
                        attachment={att}
                        isEditing={true}
                        onRemove={handleRemoveExistingAttachment}
                      />
                    ))}
                  </div>
                )}


                {/* Upload new attachments */}
                <Upload
                  listType='picture-card'
                  fileList={fileList}
                  onChange={handleFileChange}
                  accept="image/*,video/*,.heic,.heif,application/*,text/*"
                  customRequest={({ onSuccess }) => {
                    setTimeout(() => {
                      onSuccess("ok");
                    }, 0);
                  }}
                  beforeUpload={(file) => {
                    const isAccepted = file.type.startsWith('image/') ||
                      file.type.startsWith('video/') ||
                      file.type.startsWith('application/') ||
                      file.type.startsWith('text/') ||
                      file.name.toLowerCase().endsWith('.heic') ||
                      file.name.toLowerCase().endsWith('.heif');
                    if (!isAccepted) {
                      toast.error('This format is not acceptable');
                      return Upload.LIST_IGNORE;
                    }
                    return false;
                  }}>

                  {fileList.length >= 5 ? null : (
                    <div>
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  )}
                </Upload>
              </>
            )}
          </div>

          {/* Footer */}
          <div className='flex justify-between items-center pt-3 border-t border-gray-100'>
            {!isEditing ? (
              <button
                onClick={() => onOpenDetails(incident.id)}
                className='text-blue-600 hover:text-blue-800 text-sm cursor-pointer'>
                View Details →
              </button>
            ) : (
              <div className='flex gap-2'>
                <button
                  onClick={() => setIsEditing(false)}
                  className='px-3 py-1 border rounded text-sm cursor-pointer'>
                  Cancel
                </button>

                <button
                  onClick={handleSave}
                  variant='is-link'
                  disabled={loading}
                  className='px-3 py-1 border rounded text-sm border-blue-600 text-white bg-blue-400 cursor-pointer'>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </InfoWindow>
  )
}

const INCIDENT_MAP_MARKER_ICON_CACHE = {}
const INCIDENT_MAP_MARKER_SIZE = 64

function IncidentMapMarker({ text, color, icon, ...props }) {
  const iconUrl =
    INCIDENT_MAP_MARKER_ICON_CACHE[color] ||
    createFaIconMarker(INCIDENT_MAP_MARKER_SIZE, color, '\uf3c5')
  INCIDENT_MAP_MARKER_ICON_CACHE[color] = iconUrl

  return (
    <GoogleMapsMarker
      label={{
        text,
        color: 'white',
        fontWeight: '800',
        fontSize: '20px',
        className: 'dispatch-incident-map-marker-label'
      }}
      icon={{
        url: iconUrl,
        scaledSize: { width: 24, height: 24 }
      }}
      {...props}
    />
  )
}

function IncidentMapMarkersWrapper({
  incidents,
  selectedIncident,
  onClick,
}) {
  // if nothing selected → show nothing
  if (!selectedIncident) return null

  return Object.values(incidents)
    .filter((incident) => {
      if (incident.status !== 'inprogress') return false

      // must be the selected incident
      if (incident.id !== selectedIncident.id) return false

      const userAccepted = incident.acceptedBy?.some(
        (u) => u.userId === auth.currentUser.uid
      )

      // only show pins if user accepted this incident
      return userAccepted
    })
    .flatMap((incident) => {
      if (!Array.isArray(incident.mapMarkers)) return []

      return incident.mapMarkers.map((mapMarker) => {
        const { id, label, color, latitude, longitude } = mapMarker

        return (
          <IncidentMapMarker
            key={id}
            position={{ lat: latitude, lng: longitude }}
            icon="\uf3c5"
            color={color}
            text={label}
            onClick={(marker) => onClick(marker, incident, mapMarker)}
          />
        )
      })
    })
}



function AddressSearchBox({
  addressSearchBoxRef,
  leftMargin,
  mapSettings,
  setMapCenter,
  onMapClick
}) {
  const [searchBox, setSearchBox] = useState()
  const browserLocation = useBrowserLocation()

  const searchboxStyle = useMemo(
    () =>
      Object.assign({}, styles.searchBox, {
        left: leftMargin,
        opacity: leftMargin > 800 ? 0.0 : 1.0
      }),
    [leftMargin]
  )

  const bounds = useMemo(() => {
    const { defaultMapCenter } = mapSettings

    if (!defaultMapCenter) {
      return
    }

    const center = new window.google.maps.LatLng(
      (defaultMapCenter && defaultMapCenter.lat) ||
      (browserLocation && browserLocation[0]) ||
      0,
      (defaultMapCenter && defaultMapCenter.lng) ||
      (browserLocation && browserLocation[1]) ||
      0
    )

    const circle = new window.google.maps.Circle({
      radius: SEARCHBOX_BOUNDS_RADIUS_METERS,
      center
    })
    return circle.getBounds()
  }, [mapSettings, browserLocation])

  const onPlacesChanged = useCallback(() => {
    if (!searchBox) {
      return
    }

    const places = searchBox.getPlaces()
    if (places.length === 0) {
      return
    }

    const place = places[0]
    const longitude = place.geometry.location.lng()
    const latitude = place.geometry.location.lat()

    setMapCenter(place.geometry.location)
    onMapClick &&
      onMapClick({ loc: [longitude, latitude], placeId: place.place_id })
  }, [searchBox, onMapClick, setMapCenter])

  return (
    <StandaloneSearchBox
      onLoad={setSearchBox}
      onUnmount={() => setSearchBox(null)}
      onPlacesChanged={onPlacesChanged}
      bounds={bounds}>
      <input
        ref={addressSearchBoxRef}
        type='text'
        placeholder='Search by address or location...'
        style={searchboxStyle}
      />
    </StandaloneSearchBox>
  )
}

function point2LatLng(point, map) {
  var topRight = map
    .getProjection()
    .fromLatLngToPoint(map.getBounds().getNorthEast())
  var bottomLeft = map
    .getProjection()
    .fromLatLngToPoint(map.getBounds().getSouthWest())
  var scale = Math.pow(2, map.getZoom())
  var worldPoint = new window.google.maps.Point(
    point.x / scale + bottomLeft.x,
    point.y / scale + topRight.y
  )
  return map.getProjection().fromPointToLatLng(worldPoint)
}

function DraggableMapMarker({ map, selectedIncident, setMapMarkerModal }) {
  const onDrop = useCallback(
    async (e) => {
      if (!map || !selectedIncident) {
        return
      }

      const cursorPos = [e.clientX, e.clientY]

      const mapRect = map.getDiv().getBoundingClientRect()
      cursorPos[0] -= mapRect.x
      cursorPos[1] -= mapRect.y

      const coords = point2LatLng({ x: cursorPos[0], y: cursorPos[1] }, map)
      if (!coords) {
        return
      }

      const latitude = coords.lat()
      const longitude = coords.lng()

      setMapMarkerModal({ latitude, longitude })
    },
    [map, selectedIncident, setMapMarkerModal]
  )

  const [draggableProps] = useDraggable(onDrop)

  return (
    <div className='draggable-map-marker'>
      <i className='fa fa-map-marker-alt' {...draggableProps} />
    </div>
  )
}

const INCIDENT_MAP_MARKER_COLORS = ['#F44336', '#FF9800', '#4CAF50']

function CreateMapMarkerModal({
  incident,
  latitude,
  longitude,
  onMarkerCreated,
  user
}) {
  const [client] = useApi()
  const { processFile } = useHeicConverter();
  const [loading, setLoading] = useState(false)
  const setError = useError()

  const [getFieldProps, formData, formError] = useForm()

  // ===== NEW STATES FOR ATTACHMENTS =====
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewImage, setPreviewImage] = useState('')
  const [fileList, setFileList] = useState([])

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })

  const handlePreview = async (file) => {
    let previewFile = file.originFileObj;
    previewFile = await processFile(previewFile);

    const previewUrl = URL.createObjectURL(previewFile);
    setPreviewImage(previewUrl);
    setPreviewOpen(true);
  };

  const handleChange = ({ fileList: newFileList }) => setFileList(newFileList)

  const uploadButton = (
    <button style={{ border: 0, background: 'none' }} type='button'>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  )

  async function handleSubmit(e) {
    e.preventDefault()

    const { label, description, color } = formData
    const pinId = `Pin ${Date.now()}`
    const userId = auth.currentUser.uid || 'unknown'

    setLoading(true)

    try {
      const attachments = await uploadAttachments({
        fileList,
        incident,
        pinId,
        userId
      })
      const marker = await createIncidentMapMarker(incident.id, {
        latitude,
        longitude,
        label: label.toUpperCase(),
        description,
        color,
        icon: 'n/a',
        createdAt: new Date().toISOString(),
        id: pinId,
        attachments: attachments.map((att) => ({
          created_at: att.created_at,
          id: att.id,
          photo_url: att.photo_url || att.url,
          pin_id: pinId,
          user_id: userId,
          ...(att.thumbnail && { thumbnail: att.thumbnail })
          // thumbnail: att.thumbnail
        }))
      })



      onMarkerCreated && onMarkerCreated(marker)

      // ===== SYNC PIN ATTACHMENTS VIA CLOUD FUNCTION =====
      if (incident?.chatId) {
        try {
          const senderData = {
            id: user?.id || userId,
            displayName: user?.displayName || 'User',
            profileUrl: user?.profileUrl || ''
          }

          const pinLocation = {
            latitude: latitude.toString(),
            longitude: longitude.toString()
          }

          await syncPinAttachments({
            incidentId: incident.id,
            markerId: pinId,
            chatId: incident.chatId,
            oldAttachments: [],
            newAttachments: mapAttachmentsToSync(attachments),
            senderData,
            pinLocation
          })
        } catch (syncError) {
          console.error('Pin created but failed to sync attachments:', syncError)
        }
      }


    } catch (err) {
      setLoading(false)
      setError(err)
    }

  }

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        label='Label'
        name='label'
        placeholder='Label'
        maxLength={10}
        required={true}
        {...getFieldProps()}
      />

      <TextAreaField
        label='Description (optional)'
        name='description'
        placeholder='Description'
        {...getFieldProps()}
      />

      <ColorField
        label='Color'
        name='color'
        required={true}
        defaultValue={'#F44336'}
        colors={INCIDENT_MAP_MARKER_COLORS}
        {...getFieldProps()}
      />

      {/* ===== NEW ATTACHMENT FIELD ===== */}
      <div style={{ marginBottom: 16 }}>
        <label>Attachments</label>
        <Upload
          listType="picture-card"
          accept="image/*,video/*,.heic,.heif,application/*,text/*"
          fileList={fileList}
          onChange={handleChange}
          onPreview={handlePreview}
          customRequest={({ onSuccess }) => {
            setTimeout(() => {
              onSuccess("ok");
            }, 0);
          }}
          beforeUpload={async (file) => {
            const isAccepted = file.type.startsWith('image/') ||
              file.type.startsWith('video/') ||
              file.type.startsWith('application/') ||
              file.type.startsWith('text/') ||
              file.name.toLowerCase().endsWith('.heic') ||
              file.name.toLowerCase().endsWith('.heif');
            if (!isAccepted) {
              toast.error('This format is not acceptable');
              return Upload.LIST_IGNORE;
            }
            const processedFile = await processFile(file);
            return processedFile;
          }}
        >
          Upload
        </Upload>


        {previewImage && (
          <Image
            styles={{ root: { display: 'none' } }}
            preview={{
              open: previewOpen,
              onOpenChange: (visible) => setPreviewOpen(visible),
              afterOpenChange: (visible) => !visible && setPreviewImage('')
            }}
            src={previewImage}
          />
        )}
      </div>

      <div className='level'>
        <div className='level-left' />
        <div className='level-right'>
          <div className='level-item'>
            <Button
              type='submit'
              className={loading ? 'is-loading' : ''}
              enabled={`${!loading}`}>
              Create marker
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}

function TrackingControl({
  onOpenTracking,
  isTrackingActive,
  currentLocation,
  trackingPath
}) {
  const trackPoints = trackingPath.length

  return (
    <div
      className='tracking-control-wrapper'
      style={{
        position: 'absolute',
        top: '170px',
        right: '10px',
        zIndex: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        alignItems: 'flex-end'
      }}>
      {/* Location & Tracking Status */}
      <div
        style={{
          background: isTrackingActive ? '#00d084' : '#3366ff',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '10px',
          fontWeight: 'bold',
          textAlign: 'center'
        }}
        title={
          isTrackingActive
            ? `Tracking active - ${trackPoints} points recorded`
            : 'Your location is live'
        }>
        {isTrackingActive ? (
          <div className='flex items-center gap-2'>
            <i className='fa-solid fa-map-pin text-white'></i> TRACKING (
            {trackPoints})
          </div>
        ) : (
          <div className='flex items-center gap-2'>
            <i className='fa-solid fa-map-pin text-white'></i> LIVE
          </div>
        )}
      </div>

      <button
        className={`tracking-control-button ${isTrackingActive ? 'is-active' : ''
          }`}
        onClick={onOpenTracking}
        style={{
          background: isTrackingActive ? '#00d084' : '#fff',
          border: '2px solid',
          borderColor: isTrackingActive ? '#00d084' : '#ddd',
          borderRadius: '50%',
          padding: '12px',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease',
          color: isTrackingActive ? '#fff' : '#333',
          fontSize: '16px'
        }}
        title={
          isTrackingActive
            ? `Stop Tracking (${trackPoints} points)`
            : 'Start Live Tracking from current location'
        }>
        <i className={`fa ${isTrackingActive ? 'fa-stop' : 'fa-play'}`} />
      </button>
    </div>
  )
}

export default function ({
  center,
  setMapCenter,
  zoom,
  setMapZoom,
  deviceLocations,
  incidents,
  mapSelection,
  onMapClick,
  openSecondaryPanel,
  leftMargin,
  selectedIncident,
  addressSearchBoxRef,
  directionsOrigin,
  directionsDestination,
  closeSecondaryPanel
}) {
  const rest = useRest()
  const { setMap: setMapContext } = useMap()
  const tracks = TrackService(rest)
  const { socket } = useContext(ApiContext)
  const [map, setMap] = useState()
  const [infoBox, setInfoBox] = useState()
  const [mapSettings, setMapSettings] = useMapSettings()
  const [mapMarkerModal, setMapMarkerModal] = useState()
  const [trackingSidebarActive, setTrackingSidebarActive] = useState(false)
  const [isTracking, setIsTracking] = useState(false)
  const [showOtherUsers, setShowOtherUsers] = useState(true)
  const [trackingTimer, setTrackingTimer] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [trackingPath, setTrackingPath] = useState([])
  const [currentLocation, setCurrentLocation] = useState(null)
  const [locationWatchId, setLocationWatchId] = useState(null)
  const [trackingPins, setTrackingPins] = useState({
    startPin: null,
    stopPin: null,
    customPins: []
  })
  const [tracksList, setTracksList] = useState([])
  console.log("tracklist ::::>>><", tracksList)
  const [selectedTrack, setSelectedTrack] = useState(null)
  const [customPinInfo, setCustomPinInfo] = useState(null)
  const [pinModal, setPinModal] = useState(null)
  const [activeTrackId, setActiveTrackId] = useState(null)
  const [liveTrackUpdates, setLiveTrackUpdates] = useState({})
  const [client] = useApi()
  const { user } = useContext(ApiContext)
  const isTrackingRef = useRef(false)
  const isPausedRef = useRef(false)
  const activeTrackIdRef = useRef(null)
  const [infoWindowConfig, setInfoWindowConfig] = useState(null)
  const [originalStartLocation, setOriginalStartLocation] = useState(null)
  const [stopTrackModal, setStopTrackModal] = useState(false)
  const [deletePinModal, setDeletePinModal] = useState({
    open: false,
    marker: null,
    incident: null
  })
  const createPin = usePermissions('incident', 'updateAny')


  // const getIPBasedLocation = () => {
  //   return new Promise((resolve, reject) => {
  //     fetch('https://ipapi.co/json/')
  //       .then((response) => response.json())
  //       .then((data) => {
  //         if (data.latitude && data.longitude) {
  //           const location = {
  //             lat: parseFloat(data.latitude),
  //             lng: parseFloat(data.longitude),
  //             accuracy: 50000,
  //             source: 'ip',
  //             city: data.city,
  //             region: data.region,
  //             country: data.country_name
  //           }
  //           resolve(location)
  //         } else {
  //           reject(new Error('IP location not available'))
  //         }
  //       })
  //       .catch((error) => {
  //         console.warn('IP location failed:', error)
  //         reject(error)
  //       })
  //   })
  // }

  const isValidLocation = (location) => {
    return (
      location &&
      typeof location.lat === 'number' &&
      typeof location.lng === 'number' &&
      !isNaN(location.lat) &&
      !isNaN(location.lng) &&
      location.lat >= -90 &&
      location.lat <= 90 &&
      location.lng >= -180 &&
      location.lng <= 180 &&
      location.lat !== 0 &&
      location.lng !== 0
    )
  }

  const updateUserLocationViaAPI = useCallback(
    async (latitude, longitude) => {
      if (!user?.id || user.dutyStatus !== true) {
        return
      }

      try {
        const organizationId = user?.orgId
        if (!organizationId) {
          console.error('Organization ID not found')
          return
        }

        const payload = { latitude, longitude, organizationId }
        // const response = await tracks.updateLocation(payload)
      } catch (error) {
        console.error('Error updating location via API:', error)
        // toast.error('Failed to update location')
      }
    },
    [client, user?.id, user?.dutyStatus, user?.orgId]
  )

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        // getIPBasedLocation()
        //   .then(resolve)
        //   .catch((error) => {
        //     resolve(null)
        //   })
        resolve(null)
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            source: 'gps'
          }
          setCurrentLocation(location)
          resolve(location)
        },
        (error) => {
          toast.error('failed to load location')
          // getIPBasedLocation()
          //   .then((ipLocation) => {
          //     resolve(ipLocation)
          //   })
          //   .catch((ipError) => {
          //     console.error('IP location also failed:', ipError)
          //     resolve(null)
          //   })
          resolve(null)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000
        }
      )
    })
  }

  // useEffect(() => {
  //   const checkActiveTrack = async () => {
  //     if (!user?.id || !selectedIncident?.id) return

  //     try {
  //       const response = await tracks.getTracksByIncident(selectedIncident.id)
  //       if (response.data && Array.isArray(response.data)) {
  //         const userActiveTracks = response.data.filter(
  //           (track) => track.user_id === user.id && track.status === 'active'
  //         )

  //         if (userActiveTracks.length > 0) {
  //           const activeTrack = userActiveTracks[0] // Get the first active track

  //           // Restore tracking state
  //           setActiveTrackId(activeTrack.id)
  //           activeTrackIdRef.current = activeTrack.id
  //           setIsTracking(true)
  //           setIsPaused(false)
  //           isTrackingRef.current = true
  //           isPausedRef.current = false

  //           // Load existing points for this track
  //           if (activeTrack.points && activeTrack.points.length > 0) {
  //             const path = activeTrack.points.map((point) => ({
  //               lat: point.latitude,
  //               lng: point.longitude,
  //               timestamp: point.timestamp
  //             }))
  //             setTrackingPath(path)

  //             // Set current location to last point
  //             if (path.length > 0) {
  //               setCurrentLocation(path[path.length - 1])
  //             }
  //           }

  //           // Start location tracking again
  //           startLocationTracking()
  //         }
  //       }
  //     } catch (error) {
  //       console.error('Failed to check active tracks:', error)
  //     }
  //   }

  //   checkActiveTrack()
  // }, [user?.id, selectedIncident?.id, updateUserLocationViaAPI])

  // Join incident room when incident is selected
  useEffect(() => {
    if (!socket || !selectedIncident?.id) return

    socket.emit('join_incident', selectedIncident.id, (ack) => {
      if (ack?.success) {
      } else {
        console.warn(
          `⚠️ Failed to join incident room: ${selectedIncident.id}`,
          ack
        )
      }
    })

    // Load existing tracks for this incident
    loadIncidentTracks(selectedIncident.id)

    if (
      selectedIncident.acceptedBy &&
      Array.isArray(selectedIncident.acceptedBy)
    ) {
      selectedIncident.acceptedBy.forEach((acceptedUser) => {
        if (acceptedUser.userId) {
          socket.emit('join_user_location_update', acceptedUser.userId)
        }
      })
    }

    // Cleanup: Leave room when component unmounts or incident changes
    return () => {
      if (socket && selectedIncident?.id) {
        socket.emit('leave_incident', selectedIncident.id)
        // Also leave user location update rooms
        if (
          selectedIncident.acceptedBy &&
          Array.isArray(selectedIncident.acceptedBy)
        ) {
          selectedIncident.acceptedBy.forEach((acceptedUser) => {
            if (acceptedUser.userId) {
              socket.emit('leave_user_location_update', acceptedUser.userId)
            }
          })
        }
      }
    }
  }, [socket, selectedIncident?.id, selectedIncident?.acceptedBy])

  const handleRealTimeTrackUpdate = useCallback(
    (data) => {
      // Only process updates for the currently selected incident
      if (data?.incidentId !== selectedIncident?.id) {
        return
      }

      const { track, latestPoint, userId } = data

      if (!track || !track.id) {
        console.error('❌ Invalid track data received:', track)
        return
      }

      // Store live update for info windows
      setLiveTrackUpdates((prev) => ({
        ...prev,
        [track.id]: {
          ...track,
          latestPoint: latestPoint // Store latest point separately for easy access
        }
      }))

      // Update tracks list with the new track data
      setTracksList((prevTracks) => {
        const existingTrackIndex = prevTracks.findIndex(
          (t) => t.id === track.id
        )

        let updatedTracks

        if (existingTrackIndex >= 0) {
          // Update existing track - merge points intelligently
          updatedTracks = [...prevTracks]
          const existingTrack = updatedTracks[existingTrackIndex]

          // Merge points, avoiding duplicates
          const existingPoints = existingTrack.points || []
          const newPoints = track.points || []

          // Create a map of existing points by timestamp to avoid duplicates
          const pointsMap = new Map()

          // Add all existing points
          existingPoints.forEach((point) => {
            const key =
              point.timestamp || `${point.latitude}-${point.longitude}`
            pointsMap.set(key, point)
          })

          // Add latest point if it exists
          if (latestPoint) {
            const latestKey =
              latestPoint.timestamp ||
              `${latestPoint.latitude}-${latestPoint.longitude}`
            pointsMap.set(latestKey, latestPoint)
          }

          // Add new points from track (will overwrite duplicates)
          newPoints.forEach((point) => {
            const key =
              point.timestamp || `${point.latitude}-${point.longitude}`
            pointsMap.set(key, point)
          })

          // Convert back to array and sort by timestamp
          const mergedPoints = Array.from(pointsMap.values()).sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
          )

          updatedTracks[existingTrackIndex] = {
            ...existingTrack,
            ...track,
            points: mergedPoints.length > 0 ? mergedPoints : existingPoints
          }
        } else {
          // Add new track - ensure points array exists
          const points = track.points || []
          if (latestPoint) {
            points.push(latestPoint)
          }
          updatedTracks = [...prevTracks, { ...track, points }]
        }

        return updatedTracks
      })
    },
    [selectedIncident?.id, user?.id, map]
  )

  // Listen for real-time track updates
  useEffect(() => {
    if (!socket) return

    const handleTrackUpdate = (data) => {
      // Extract the payload from the nested structure
      const trackData = data

      // Handle the real-time update
      handleRealTimeTrackUpdate(trackData)
    }

    // Listen for track updates using the socket from context
    socket.on('add_points_track', handleTrackUpdate)

    // Cleanup the event listener
    return () => {
      if (socket) {
        socket.off('add_points_track', handleTrackUpdate)
      }
    }
  }, [socket, handleRealTimeTrackUpdate, selectedIncident?.id, selectedTrack])

  useEffect(() => {
    if (!socket) return

    const handleTrackNameUpdated = (data) => {
      if (data?.incidentId !== selectedIncident?.id) {
        return
      }

      const { id, name } = data?.track

      // Update tracks list
      setTracksList((prevTracks) =>
        prevTracks.map((track) =>
          track.id === id ? { ...track, name } : track
        )
      )

      // Update live track updates
      setLiveTrackUpdates((prev) => {
        if (prev[id]) {
          return {
            ...prev,
            [id]: {
              ...prev[id],
              name
            }
          }
        }
        return prev
      })

      // Update selected track if it's the current one
      if (selectedTrack?.id === id) {
        setSelectedTrack((prev) => (prev ? { ...prev, name } : null))
      }
    }

    socket.on('change_track_name', handleTrackNameUpdated)

    return () => {
      if (socket) {
        socket.off('change_track_name', handleTrackNameUpdated)
      }
    }
  }, [socket, selectedIncident?.id, selectedTrack?.id])

  // Add this after the change_track_name useEffect in your main Map component
  useEffect(() => {
    if (!socket) return

    const handleTrackColorUpdated = (data) => {
      if (data?.incidentId !== selectedIncident?.id) {
        return
      }

      const { id, color_code } = data?.track

      // Update tracks list
      setTracksList((prevTracks) =>
        prevTracks.map((track) =>
          track.id === id ? { ...track, color_code } : track
        )
      )

      // Update live track updates
      setLiveTrackUpdates((prev) => {
        if (prev[id]) {
          return {
            ...prev,
            [id]: {
              ...prev[id],
              color_code
            }
          }
        }
        return prev
      })

      // Update selected track if it's the current one
      if (selectedTrack?.id === id) {
        setSelectedTrack((prev) => (prev ? { ...prev, color_code } : null))
      }
    }

    socket.on('change_track_color', handleTrackColorUpdated)

    return () => {
      if (socket) {
        socket.off('change_track_color', handleTrackColorUpdated)
      }
    }
  }, [socket, selectedIncident?.id, selectedTrack?.id])

  // Add this useEffect to handle end_track events
  useEffect(() => {
    if (!socket) return

    const handleEndTrack = (data) => {
      if (data?.incidentId !== selectedIncident?.id) return
      const { track, userId } = data
      if (!track?.id) return

      // ----------- Update live track updates & list -----------
      setLiveTrackUpdates((prev) => ({
        ...prev,
        [track.id]: {
          ...prev[track.id],
          status: 'ended', // Ensure status is set to 'ended'
          ended_at: track.ended_at,
          total_duration: track.total_duration,
          total_distance: track.total_distance
        }
      }))

      setTracksList((prevTracks) =>
        prevTracks.map((t) =>
          t.id === track.id ? { ...t, ...track, status: 'ended' } : t
        )
      )

      if (selectedTrack?.id === track.id) {
        setSelectedTrack((prev) =>
          prev ? { ...prev, ...track, status: 'ended' } : null
        )
      }

      // ----------- ONLY SET PINS FOR OTHER USERS, NOT FOR CURRENT USER -----------
      if (track?.points?.length > 0 && userId !== user?.id) {
        const firstPoint = track.points[0]
        const lastPoint = track.points[track.points.length - 1]

        const startLocation = {
          lat: firstPoint.latitude,
          lng: firstPoint.longitude
        }
        const endLocation = {
          lat: lastPoint.latitude,
          lng: lastPoint.longitude
        }

        setTrackingPins((prev) => ({
          ...prev,
          startPin: {
            position: startLocation,
            type: 'start',
            timestamp: track.started_at || data.timestamp,
            trackId: track.id,
            userId
          },
          stopPin: {
            position: endLocation,
            type: 'stop',
            timestamp: track.ended_at || data.timestamp,
            trackId: track.id,
            userId
          }
        }))
      }
    }

    socket.on('end_track', handleEndTrack)

    return () => {
      socket.off('end_track', handleEndTrack)
    }
  }, [socket, selectedIncident?.id, selectedTrack?.id, user?.id]) // Add user?.id dependency

  // Add this useEffect after your existing socket useEffect for tracks
  useEffect(() => {
    if (!socket) return

    // In the socket useEffect for pin updates, update the handlePinUpdate function:
    // In the socket useEffect for pin updates, update the handlePinUpdate function:

    const handlePinUpdate = (data) => {
      // Extract the payload from the nested structure
      const pinData = data

      // Only process updates for the currently selected incident
      if (pinData?.incidentId !== selectedIncident?.id) {
        return
      }

      // Update pins list with the new pin data
      setTrackingPins((prev) => {
        const currentCustomPins = Array.isArray(prev.customPins)
          ? prev.customPins
          : []

        // Check if pin already exists
        const existingPinIndex = currentCustomPins.findIndex(
          (p) => p.id === pinData.pin?.id
        )

        let updatedCustomPins

        if (existingPinIndex >= 0) {
          // Update existing pin
          updatedCustomPins = [...currentCustomPins]

          // Handle both old photo_url and new photos array
          // NEW: Handle attachments payload
          const images = []

          if (
            pinData.pin.attachments &&
            Array.isArray(pinData.pin.attachments)
          ) {
            pinData.pin.attachments.forEach((attachment, index) => {
              if (attachment.photo_url) {
                images.push({
                  url: attachment.photo_url,
                  name: `pin-image-${index}`,
                  type: 'image/jpeg',
                  id: attachment.id || null
                })
              }
            })
          }

          updatedCustomPins[existingPinIndex] = {
            ...updatedCustomPins[existingPinIndex],
            ...pinData.pin,
            position: {
              lat: pinData.pin.latitude,
              lng: pinData.pin.longitude
            },
            type: 'custom',
            label: pinData.pin.name,
            description: pinData.pin.description,
            color:
              pinData?.pin?.color?.length === 9
                ? `#${pinData?.pin.color.slice(3)}`
                : pinData?.pin?.color || '#eb144c',
            images:
              images.length > 0
                ? images
                : updatedCustomPins[existingPinIndex].images
          }
        } else {
          // Add new pin - handle both old and new image formats
          const images = []
          if (
            pinData.pin.attachments &&
            Array.isArray(pinData.pin.attachments)
          ) {
            pinData.pin.attachments.forEach((attachment, index) => {
              if (attachment.photo_url) {
                images.push({
                  url: attachment.photo_url,
                  thumbnail: attachment.thumbnail,
                  name: attachment.name || `pin-image-${index}`,
                  fileType: attachment.fileType || attachment.type || 'image/jpeg',
                  fileSize: attachment.fileSize || attachment.size || 0,
                  id: attachment.id || null
                })
              }
            })
          }

          const newPin = {
            id: pinData.pin?.id,
            position: {
              lat: pinData.pin?.latitude,
              lng: pinData.pin?.longitude
            },
            type: 'custom',
            label: pinData.pin?.name,
            description: pinData.pin?.description,
            color:
              pinData?.pin?.color?.length === 9
                ? `#${pinData?.pin.color.slice(3)}`
                : pinData?.pin?.color || '#eb144c',
            timestamp: pinData.pin?.created_at || new Date().toISOString(),
            images: images,
            user_id: pinData.pin?.user_id
          }
          updatedCustomPins = [...currentCustomPins, newPin]
        }

        return {
          ...prev,
          customPins: updatedCustomPins
        }
      })
    }

    // Listen for pin updates using the socket from context
    socket.on('add_pin_track', handlePinUpdate)

    // Cleanup the event listener
    return () => {
      if (socket) {
        socket.off('add_pin_track', handlePinUpdate)
      }
    }
  }, [socket, selectedIncident?.id, user?.id])

  // Replace your socket useEffect for start/end tracking with this:
  useEffect(() => {
    if (!socket) return

    // Handle track start from other users
    const handleTrackStart = async (data) => {
      if (data?.incidentId !== selectedIncident?.id) return

      const { track, userId } = data

      // Only update pins if it's NOT the current user
      if (userId !== user?.id) {
        let startingLocation = currentLocation
        if (!startingLocation) {
          startingLocation = await getCurrentLocation()
        }

        setTrackingPins((prev) => ({
          ...prev,
          startPin: {
            position: startingLocation,
            type: 'start',
            timestamp: data.timestamp || new Date().toISOString(),
            trackId: track?.id,
            userId: userId
          },
          stopPin: null
        }))
      }
    }

    socket.on('start_track', handleTrackStart)

    return () => {
      if (socket) {
        socket.off('start_track', handleTrackStart)
      }
    }
  }, [socket, selectedIncident?.id, user?.id]) // Add user?.id dependency

  useEffect(() => {
    if (!socket) return

    const handleTrackRemoved = (data) => {
      if (data?.incidentId !== selectedIncident?.id) {
        return
      }

      const { id, user_id } = data?.track

      if (!id) {
        console.error('❌ No trackId in remove_track event')
        return
      }

      setTracksList((prevTracks) => {
        const filteredTracks = prevTracks.filter((track) => track.id !== id)
        return filteredTracks
      })

      setTrackingPins((prev) => ({
        ...prev,
        customPins: prev.customPins.filter((pin) => pin.track_id !== id)
      }))

      if (selectedTrack?.id === id) {
        setSelectedTrack(null)
        setInfoBox(null)
      }
    }

    socket.on('remove_track', handleTrackRemoved)

    return () => {
      if (socket) {
        socket.off('remove_track', handleTrackRemoved)
      }
    }
  }, [socket, selectedIncident?.id, user?.id, selectedTrack])

  useEffect(() => {
    if (!socket) return

    const handlePinUpdated = (data) => {
      if (data?.incidentId !== selectedIncident?.id) {
        return
      }

      const { pin } = data

      setTrackingPins((prev) => {
        const currentCustomPins = Array.isArray(prev.customPins)
          ? prev.customPins
          : []

        const updatedCustomPins = currentCustomPins.map((p) =>
          p.id === pin.id
            ? {
              ...p,
              label: pin.name,
              description: pin.description,
              position: {
                lat: pin.latitude,
                lng: pin.longitude
              },
              images:
                pin.attachments && Array.isArray(pin.attachments)
                  ? pin.attachments
                    .filter((a) => a.photo_url)
                    .map((a, index) => ({
                      url: a.photo_url,
                      thumbnail: a.thumbnail,
                      name: `pin-image-${index}`,
                      fileType: a.fileType || a.type || 'image/jpeg',
                      fileSize: a.fileSize || a.size || 0,
                      id: a.id || null
                    }))
                  : p.images
            }
            : p
        )

        return {
          ...prev,
          customPins: updatedCustomPins
        }
      })

      if (customPinInfo?.pin?.id === pin.id) {
        setCustomPinInfo((prev) => ({
          ...prev,
          pin: {
            ...prev.pin,
            label: pin.name,
            description: pin.description,
            images:
              pin.attachments && Array.isArray(pin.attachments)
                ? pin.attachments
                  .filter((a) => a.photo_url)
                  .map((a, index) => ({
                    url: a.photo_url,
                    name: `pin-image-${index}`,
                    type: 'image/jpeg',
                    id: a.id || null
                  }))
                : prev.pin.images
          }
        }))
      }
    }

    socket.on('update_pin_track', handlePinUpdated)

    return () => {
      if (socket) {
        socket.off('update_pin_track', handlePinUpdated)
      }
    }
  }, [socket, selectedIncident?.id, user?.id, customPinInfo])

  useEffect(() => {
    if (!socket) return

    const handlePinDeleted = (data) => {
      if (data?.incidentId !== selectedIncident?.id) {
        return
      }

      const { id } = data?.pin

      setTrackingPins((prev) => {
        const currentCustomPins = Array.isArray(prev.customPins)
          ? prev.customPins
          : []

        const filteredCustomPins = currentCustomPins.filter((p) => p.id !== id)

        return {
          ...prev,
          customPins: filteredCustomPins
        }
      })

      if (customPinInfo?.pin?.id === id) {
        setCustomPinInfo(null)
      }
    }

    socket.on('remove_pin_track', handlePinDeleted)

    return () => {
      if (socket) {
        socket.off('remove_pin_track', handlePinDeleted)
      }
    }
  }, [socket, selectedIncident?.id, user?.id, customPinInfo])

  useEffect(() => {
    activeTrackIdRef.current = activeTrackId
  }, [activeTrackId])

  const onCustomPinClick = useCallback((marker, pin) => {
    setInfoBox(null)
    setInfoWindowConfig(null)

    setCustomPinInfo({
      pin: pin,
      anchor: marker
    })
  }, [])

  const checkLocationPermissions = async () => {
    try {
      const result = await navigator.permissions.query({ name: 'geolocation' })

      if (result.state === 'denied') {
        // toast.error(
        //   'Location access is blocked. Please enable location permissions in your browser.'
        // )
      } else if (result.state === 'prompt') {
        toast.info('Please allow location access when prompted.')
      }

      return result.state
    } catch (error) {
      console.error('Permission check error:', error)
    }
  }

  useEffect(() => {
    return () => {
      if (locationWatchId) {
        navigator.geolocation.clearWatch(locationWatchId)
      }
    }
  }, [locationWatchId])

  useEffect(() => {
    checkLocationPermissions()
  }, [])

  // useEffect(() => {
  //   // Don't start location if user not on duty or no user
  //   if (!user?.id || user.dutyStatus !== true) {
  //     return
  //   }

  //   // First, try to get location immediately
  //   const initializeLocation = async () => {
  //     try {
  //       const location = await getCurrentLocation()
  //       if (location) {
  //         setCurrentLocation(location)

  //         // Update API with initial location
  //         try {
  //           await updateUserLocationViaAPI(location.lat, location.lng)
  //         } catch (error) {
  //           console.error('Failed to update location via API:', error)
  //         }
  //       }
  //     } catch (error) {
  //       console.error('Failed to get initial location:', error)
  //     }
  //   }

  //   initializeLocation()

  //   // Then set up continuous tracking (only if geolocation supported)
  //   let watchId = null

  //   if (navigator.geolocation) {
  //     let lastApiCallTime = 0
  //     const MIN_TIME_MS = 10000 // Update API every 10 seconds minimum

  //     watchId = navigator.geolocation.watchPosition(
  //       async (position) => {
  //         const newLocation = {
  //           lat: position.coords.latitude,
  //           lng: position.coords.longitude,
  //           accuracy: position.coords.accuracy,
  //           timestamp: new Date().toISOString(),
  //           source: 'gps' // ✅ Always include source
  //         }

  //         // ALWAYS update current location for display
  //         setCurrentLocation(newLocation)

  //         // Update API with throttling (every 10 seconds minimum)
  //         if (user?.id && user.dutyStatus === true) {
  //           const now = Date.now()
  //           const timeSinceLastCall = now - lastApiCallTime

  //           if (timeSinceLastCall > MIN_TIME_MS) {
  //             try {
  //               await updateUserLocationViaAPI(newLocation.lat, newLocation.lng)
  //               lastApiCallTime = now
  //             } catch (error) {
  //               console.error('Failed to update location via API:', error)
  //             }
  //           }
  //         }
  //       },
  //       (error) => {
  //         console.error('Continuous GPS tracking error:', error)
  //         // Don't toast here - let getCurrentLocation handle errors
  //       },
  //       {
  //         enableHighAccuracy: true,
  //         timeout: 15000,
  //         maximumAge: 10000, // Accept cached location up to 10 seconds old
  //         distanceFilter: 1 // Get updates even for 1 meter movement
  //       }
  //     )

  //     setLocationWatchId(watchId)
  //   }

  //   return () => {
  //     if (watchId) {
  //       navigator.geolocation.clearWatch(watchId)
  //     }
  //   }
  // }, [user?.id, user?.dutyStatus, updateUserLocationViaAPI]) // Add updateUserLocationViaAPI to dependencies

  // Add this useEffect after the existing useEffect for continuous location tracking
  // useEffect(() => {
  //   if (!user?.id || user.dutyStatus !== true || !currentLocation) {
  //     return
  //   }

  //   const intervalId = setInterval(() => {
  //     // Send location update every 30 seconds even if user hasn't moved
  //     updateUserLocationViaAPI(currentLocation.lat, currentLocation.lng)
  //   }, 3000) // Every 30 seconds

  //   return () => {
  //     clearInterval(intervalId)
  //   }
  // }, [user?.id, user?.dutyStatus, currentLocation, updateUserLocationViaAPI])

  const loadIncidentTracks = useCallback(
    async (incidentId) => {
      if (!incidentId) return

      try {
        const response = await tracks.getTracksByIncident(incidentId)
        console.log("response is here :::>>><<<", response)
        if (response.data && Array.isArray(response.data)) {
          setTracksList(response.data)
        }
      } catch (error) {
        console.error('Failed to load incident tracks:', error)
      }
    },
    [tracks]
  )

  useEffect(() => {
    if (selectedIncident?.id) {
      loadIncidentPins(selectedIncident.id)
      loadIncidentTracks(selectedIncident.id)
    } else {
      setTrackingPins((prev) => ({
        ...prev,
        customPins: []
      }))
      setCustomPinInfo(null)
    }
  }, [selectedIncident?.id])

  useEffect(() => {
    if (!selectedIncident?.id) {
      setTracksList([])
      setSelectedTrack(null)
    }
  }, [selectedIncident?.id])

  const onTrackClick = useCallback(
    (track) => {
      const middleIndex = Math.floor(track.points.length / 2)
      const middlePoint = track.points[middleIndex]

      if (!middlePoint) return

      const anchorMarker = new window.google.maps.Marker({
        position: { lat: middlePoint.latitude, lng: middlePoint.longitude },
        map: map,
        visible: false
      })

      // Clear all other info windows
      setInfoBox(null)
      // setCustomPinInfo(null)

      setSelectedTrack(track)
      setInfoWindowConfig({
        track: track,
        anchor: anchorMarker,
        onClose: () => {
          setSelectedTrack(null)
          setInfoWindowConfig(null)
          anchorMarker.setMap(null)
        }
      })
    },
    [map, tracks, user?.id, liveTrackUpdates]
  )

  const loadIncidentPins = useCallback(
    async (incidentId) => {
      if (!incidentId) return

      try {
        const response = await tracks.getPinsByIncident(incidentId)
        if (response.data && Array.isArray(response.data)) {
          // Convert API pins to our format
          const apiPins = response.data.map((pin) => {
            // Extract images from attachments
            const images = []
            if (pin.attachments && Array.isArray(pin.attachments)) {
              pin.attachments.forEach((attachment, index) => {
                if (attachment.photo_url) {
                  images.push({
                    url: attachment.photo_url,
                    thumbnail: attachment.thumbnail,
                    name: attachment.name || `pin-image-${index}`,
                    fileType: attachment.fileType || attachment.type || 'image/jpeg',
                    fileSize: attachment.fileSize || attachment.size,
                    id: attachment.id
                  })
                }
              })
            }

            return {
              id: pin.id,
              position: {
                lat: pin.latitude,
                lng: pin.longitude
              },
              type: 'custom',
              label: pin.name,
              description: pin.description,
              color:
                pin?.color?.length === 9
                  ? `#${pin.color.slice(3)}`
                  : pin?.color || '#eb144c',
              timestamp: pin.created_at || new Date().toISOString(),
              images: images,
              user_id: pin.user_id,
              incident_id: pin.incident_id,
              track_id: pin.track_id
            }
          })

          setTrackingPins((prev) => ({
            ...prev,
            customPins: apiPins
          }))
        }
      } catch (error) {
        console.error('Failed to load incident pins:', error)
      }
    },
    [tracks]
  ) // Add tracks as dependency

  useEffect(() => {
    if (selectedIncident?.id) {
      loadIncidentPins(selectedIncident.id)
    } else {
      // Clear pins when no incident selected
      setTrackingPins((prev) => ({
        ...prev,
        customPins: []
      }))
      setCustomPinInfo(null)
    }
  }, [selectedIncident?.id]) // Remove loadIncidentPins from dependencies

  useEffect(() => {
    return () => {
      if (locationWatchId) {
        navigator.geolocation.clearWatch(locationWatchId)
      }
    }
  }, [locationWatchId])

  useEffect(() => {
    let intervalId = null

    if (isTracking && !isPaused) {
      intervalId = setInterval(() => {
        setTrackingTimer((prev) => prev + 1)
      }, 1000)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [isTracking, isPaused])

  useEffect(() => {
    if (!mapSettings.defaultMapCenter || !mapSettings.defaultMapZoom) {
      setMapSettings({
        ...mapSettings,
        defaultMapCenter: center,
        defaultMapZoom: zoom
      })
    }
  }, [mapSettings, setMapSettings, center, zoom])

  const onClick = useCallback(
    (e) => {
      const latitude = e.latLng.lat()
      const longitude = e.latLng.lng()
      const placeId = e.placeId

      // Clear ALL info windows when clicking on empty map area
      setInfoBox(null)
      // setCustomPinInfo(null)
      setInfoWindowConfig(null)

      onMapClick && onMapClick({ loc: [longitude, latitude], placeId })
    },
    [onMapClick, setInfoBox]
  )

  const onIncidentMarkerClick = useCallback(
    (marker, incident) => {
      // Clear all other info windows
      setInfoBox(null)
      // setCustomPinInfo(null)
      setInfoWindowConfig(null)

      // Then set the current one
      setInfoBox(
        <IncidentInfoWindow
          incident={incident}
          anchor={marker}
          onClose={() => setInfoBox(null)}
          onOpenDetails={() =>
            openSecondaryPanel(
              <IncidentDetails id={incident.id} onClose={closeSecondaryPanel} />, null
              // incident.name
            )
          }
        />
      )
    },
    [setInfoBox, openSecondaryPanel]
  )

  const onDeleteMarker = useCallback(
    async (marker, incident) => {
      // Open confirmation modal instead of direct delete
      setDeletePinModal({
        open: true,
        marker,
        incident
      })
    },
    [] // Remove client dependency
  )

  const handleConfirmDeletePin = useCallback(async () => {
    const { marker, incident } = deletePinModal

    if (!marker || !incident) return

    try {
      // Sync with chat: Tell the Cloud Function that the pin is being deleted by sending an empty attachments list
      if (incident?.chatId) {
        try {
          // Relies on same mapAttachmentsToSync logic defined elsewhere
          const oldAttachments = mapAttachmentsToSync(marker.attachments || [])

          const senderData = {
            id: user?.id || auth.currentUser.uid,
            displayName: user?.displayName || 'User',
            profileUrl: user?.profileUrl || ''
          }

          const pinLocation = {
            latitude: marker.latitude.toString(),
            longitude: marker.longitude.toString()
          }

          await syncPinAttachments({
            incidentId: incident.id,
            markerId: marker.id,
            chatId: incident.chatId,
            oldAttachments: oldAttachments,
            newAttachments: [], // Empty means delete existing messages
            senderData,
            pinLocation
          })

          // NEW: Also delete the main pin message from chat
          await deletePinMessageFromChat(incident.chatId, marker.id);
        } catch (syncError) {
          console.error('Failed to sync pin deletion with chat:', syncError)
        }
      }


      await deleteIncidentMapMarker(incident.id, marker.id)
      toast.success('Map marker deleted successfully')

      // Close the info window if it's showing the deleted marker
      setInfoBox(null)

      // Close the modal
      setDeletePinModal({ open: false, marker: null, incident: null })
    } catch (err) {
      console.error('Delete marker failed:', err)
      toast.error('Failed to delete marker')
    }
  }, [deletePinModal])

  const onIncidentMapMarkerClick = useCallback(
    (marker, incident, mapMarker) => {
      // Clear all other info windows
      setInfoBox(null)
      // setCustomPinInfo(null)
      setInfoWindowConfig(null)

      // Then set the current one
      setInfoBox(
        <IncidentMapMarkerInfoWindow
          incident={incident}
          mapMarker={mapMarker}
          anchor={marker}
          onClose={() => setInfoBox(null)}
          onDeleteMarker={onDeleteMarker}
          onOpenDetails={() =>
            openSecondaryPanel(
              <IncidentDetails id={incident.id} onClose={closeSecondaryPanel} />,
              incident.name
            )
          }
          user={user}
        />
      )
    },
    [setInfoBox, openSecondaryPanel, onDeleteMarker]
  )

  const startLocationTracking = () => {
    if (!navigator.geolocation || user.dutyStatus !== true) {
      toast.error('Geolocation not supported or duty status is inactive')
      return null
    }

    // Keep track of the last recorded location for distance comparison
    let lastRecordedLocation = currentLocation

    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          timestamp: new Date().toISOString(),
          accuracy: position.coords.accuracy
        }

        // Keep current location updated for the marker
        setCurrentLocation(newLocation)

        // Use REFS instead of state
        if (isTrackingRef.current === true && isPausedRef.current === false) {
          // Calculate distance from last recorded point (in meters)
          if (lastRecordedLocation) {
            const distance = calculateDistanceInMeters(
              lastRecordedLocation.lat,
              lastRecordedLocation.lng,
              newLocation.lat,
              newLocation.lng
            )

            // Only add point if moved at least 10 meters
            if (distance < 3) {
              // Update current location marker but don't add to tracking line
              if (map) {
                map.panTo(newLocation)
              }
              return // Skip adding this point to the tracking path
            }
          }

          // Update last recorded location with new point
          lastRecordedLocation = newLocation

          setTrackingPath((prev) => {
            const newPath = [...prev, newLocation]

            // Use the ref instead of state variable to avoid stale closure
            if (activeTrackIdRef.current) {
              syncTrackingPoints(activeTrackIdRef.current, newLocation)
            }

            return newPath
          })
        }

        // Update Firebase only when on duty
        if (user?.id && user.dutyStatus === true) {
          try {
            // await updateUserLocation(user.id, newLocation)
            // await updateUserLocationViaAPI(newLocation.lat, newLocation.lng)
          } catch (error) {
            console.error('Failed to save tracking location:', error)
          }
        }

        // Keep map centered only when actively tracking (not paused)
        if (
          map &&
          isTrackingRef.current === true &&
          isPausedRef.current === false
        ) {
          map.panTo(newLocation)
        }
      },
      (error) => {
        console.error('Tracking location error:', error)
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
        distanceFilter: 5 // This ensures we get updates even for small movements
      }
    )

    setLocationWatchId(watchId)
    return watchId
  }

  // const startLocationTracking = () => {
  //   if (!navigator.geolocation || user.dutyStatus !== true) {
  //     toast.error('Geolocation not supported or duty status is inactive')
  //     return null
  //   }

  //   const watchId = navigator.geolocation.watchPosition(
  //     async (position) => {
  //       const newLocation = {
  //         lat: position.coords.latitude,
  //         lng: position.coords.longitude,
  //         timestamp: new Date().toISOString(),
  //         accuracy: position.coords.accuracy
  //       }

  //       // Keep current location updated
  //       setCurrentLocation(newLocation)

  //       // Use REFS instead of state - THIS IS THE KEY FIX
  //       if (isTrackingRef.current === true && isPausedRef.current === false) {
  //         setTrackingPath((prev) => {
  //           const newPath = [...prev, newLocation]

  //           // Use the ref instead of state variable to avoid stale closure
  //           if (activeTrackIdRef.current) {
  //             syncTrackingPoints(activeTrackIdRef.current, newLocation)
  //           }

  //           return newPath
  //         })
  //       }

  //       // Update Firebase only when on duty
  //       if (user?.id && user.dutyStatus === true) {
  //         try {
  //           await updateUserLocation(user.id, newLocation)
  //         } catch (error) {
  //           console.error('Failed to save tracking location:', error)
  //         }
  //       }

  //       // Keep map centered only when actively tracking (not paused)
  //       if (
  //         map &&
  //         isTrackingRef.current === true &&
  //         isPausedRef.current === false
  //       ) {
  //         map.panTo(newLocation)
  //       }
  //     },
  //     (error) => {
  //       console.error('Tracking location error:', error)
  //     },
  //     {
  //       enableHighAccuracy: true,
  //       timeout: 5000,
  //       maximumAge: 0,
  //       distanceFilter: 1
  //     }
  //   )

  //   setLocationWatchId(watchId)
  //   return watchId
  // }

  // Stop tracking location
  const stopLocationTracking = () => {
    if (locationWatchId) {
      navigator.geolocation.clearWatch(locationWatchId)
      setLocationWatchId(null)
    }
    setCurrentLocation(null)
  }

  // Clear tracking data
  const clearTrackingData = () => {
    setTrackingPath([])
    setCurrentLocation(null)
  }

  const mapOptions = useMemo(() => {
    const { ROADMAP, SATELLITE, HYBRID, TERRAIN } = window.google.maps.MapTypeId
    const { RIGHT_TOP } = window.google.maps.ControlPosition

    let defaultMapTypeId = HYBRID
    switch (mapSettings.defaultMapType) {
      case 'roadmap':
      case 'roads':
        defaultMapTypeId = ROADMAP
        break
      case 'satellite':
      default:
        defaultMapTypeId = HYBRID
        break
      case 'terrain':
        defaultMapTypeId = TERRAIN
        break
    }

    return {
      zoomControl: true,
      panControl: false,
      rotateControl: false,
      mapTypeId: defaultMapTypeId,
      mapTypeControl: true,
      mapTypeControlOptions: {
        mapTypeIds: [ROADMAP, HYBRID, TERRAIN],
        position: RIGHT_TOP
      }
    }
  }, [mapSettings])

  const dragging = useRef(false)

  useEffect(() => {
    if (!map || !map.center) {
      return
    }

    if (!center) {
      const incidentLocations = Object.values(incidents)
        .filter((incident) => incident.status === 'inprogress')
        .map((incident) => {
          const loc = incident.locations.find(
            (loc) => loc.format === 'lat-long'
          )
          return (
            loc && new window.google.maps.LatLng(loc.latitude, loc.longitude)
          )
        })
        .filter((x) => x)
      const incidentMapMarkerLocations = Object.values(incidents)
        .filter((incident) => incident.status === 'inprogress')
        .map((incident) =>
          (incident.mapMarkers || []).map(
            ({ latitude, longitude }) =>
              new window.google.maps.LatLng(latitude, longitude)
          )
        )
        .flat()
      const flatDeviceLocations = Object.entries(deviceLocations).map(
        ([, { loc }]) => new window.google.maps.LatLng(loc[1], loc[0])
      )
      const bounds = new window.google.maps.LatLngBounds()

      for (const loc of incidentLocations) {
        bounds.extend(loc)
      }
      for (const loc of incidentMapMarkerLocations) {
        bounds.extend(loc)
      }
      for (const loc of flatDeviceLocations) {
        bounds.extend(loc)
      }

      map.setCenter(bounds.getCenter())
      map.fitBounds(bounds)
      return
    }

    if (!dragging.current) {
      if (map.center.lat() === center.lat && map.center.lng() === center.lng) {
        return
      }

      map.panTo(center)
    }
  }, [map, center, incidents, deviceLocations])

  const cachedCenter = useRef(center)

  // API Integration Functions
  const startTrackSession = async (startingLocation) => {
    try {
      if (!selectedIncident) {
        throw new Error('No incident selected for tracking')
      }

      const response = await tracks.startTrack(
        selectedIncident.id,
        user?.orgId // Default fallback organization ID
      )

      if (response && response?.data?.id) {
        setActiveTrackId(response?.data?.id)
        activeTrackIdRef.current = response?.data?.id
        return response?.data?.id
      } else {
        throw new Error('Invalid response from startTrack API')
      }
    } catch (error) {
      console.error('Failed to start track session:', error)
      return null // Return null to indicate failure
      // console.error('Failed to start track session:', error)
      // const localTrackId = `local-${Date.now()}`
      // setActiveTrackId(localTrackId)
      // activeTrackIdRef.current = localTrackId
      // return localTrackId
    }
  }

  const pauseTrackSession = async () => {
    if (!activeTrackId) return false

    try {
      const res = await tracks.pauseTrack(activeTrackId)
      return true // indicate success
    } catch (error) {
      console.error('Failed to pause track session:', error)
      return false // indicate failure
    }
  }

  const resumeTrackSession = async () => {
    if (!activeTrackId) return false

    try {
      await tracks.resumeTrack(activeTrackId)
      return true // API succeeded
    } catch (error) {
      console.error('Failed to resume track session:', error)
      return false // API failed
    }
  }

  const endTrackSession = async (trackName = null) => {
    if (!activeTrackId) return false

    try {
      // No need to sync points here since we're syncing in real-time
      await tracks.endTrack(activeTrackId, trackName)
      return true
    } catch (error) {
      console.error('Failed to end track session:', error)
      return false
      // Continue with local end even if API fails
    }
  }

  const syncTrackingPoints = async (trackId, point) => {
    if (!trackId) return

    try {
      const pointData = {
        latitude: point.lat,
        longitude: point.lng,
        timestamp: point.timestamp,
        accuracy: point.accuracy || 0
      }

      await tracks.addPoints(trackId, [pointData]) // Send as array with single point
    } catch (error) {
      console.error('Failed to sync tracking point:', error)
    }
  }

  // const startTracking = async () => {
  //   try {
  //     let startingLocation = currentLocation
  //     if (!startingLocation) {
  //       startingLocation = await getCurrentLocation()
  //     }

  //     if (
  //       !startingLocation ||
  //       typeof startingLocation.lat !== 'number' ||
  //       typeof startingLocation.lng !== 'number'
  //     ) {
  //       throw new Error('Invalid starting location')
  //     }

  //     // Start track session via API
  //     const trackId = await startTrackSession(startingLocation)

  //     const initialPath = [startingLocation]
  //     setTrackingPath(initialPath)

  //     // STORE THE ORIGINAL START LOCATION
  //     setOriginalStartLocation(startingLocation)

  //     // SYNC THE STARTING POINT IMMEDIATELY
  //     if (trackId) {
  //       syncTrackingPoints(trackId, startingLocation)
  //     }

  //     setTrackingPins((prev) => ({
  //       ...prev,
  //       startPin: null,
  //       stopPin: null
  //     }))

  //     setIsTracking(true)
  //     setIsPaused(false)
  //     isTrackingRef.current = true
  //     isPausedRef.current = false
  //     setTrackingTimer(0)

  //     // START REAL LOCATION TRACKING FOR CONTINUOUS SYNC
  //     startLocationTracking()

  //     simulateDummyTracking(startingLocation)

  //     // toast.success('Live tracking started from your current location')
  //   } catch (error) {
  //     console.error('Failed to start tracking:', error)

  //     const fallbackLocation = { lat: 21.2021622, lng: 72.7998363 }

  //     // Start local track session as fallback
  //     const localTrackId = `local-${Date.now()}`
  //     setActiveTrackId(localTrackId)
  //     activeTrackIdRef.current = localTrackId
  //     setCurrentLocation(fallbackLocation)
  //     setTrackingPath([fallbackLocation])

  //     // STORE THE ORIGINAL START LOCATION FOR FALLBACK TOO
  //     setOriginalStartLocation(fallbackLocation)

  //     setTrackingPins({
  //       startPin: {
  //         position: fallbackLocation,
  //         type: 'start',
  //         timestamp: new Date().toISOString()
  //       },
  //       stopPin: null,
  //       customPins: []
  //     })

  //     setIsTracking(true)
  //     setIsPaused(false)
  //     isTrackingRef.current = true
  //     isPausedRef.current = false
  //     setTrackingTimer(0)

  //     // START REAL LOCATION TRACKING FOR CONTINUOUS SYNC (even for local)
  //     startLocationTracking()

  //     simulateDummyTracking(fallbackLocation)
  //     // toast.success('Live tracking started with default location')
  //   }
  // }

  // const simulateDummyTracking = (startLocation) => {
  //   if (!startLocation) {
  //     console.error('No start location for dummy tracking')
  //     return
  //   }

  //   // Clear any existing interval
  //   if (dummyIntervalId) {
  //     clearInterval(dummyIntervalId)
  //     setDummyIntervalId(null)
  //   }

  //   // When resuming, we continue from the provided startLocation (which should be the last point)
  //   let currentLat = startLocation.lat
  //   let currentLng = startLocation.lng

  //   const intervalId = setInterval(() => {
  //     if (isTrackingRef.current && !isPausedRef.current) {
  //       // Generate next point by making small incremental changes
  //       currentLat += 0.0005
  //       currentLng += 0.0005

  //       const newPoint = {
  //         lat: parseFloat(currentLat.toFixed(6)),
  //         lng: parseFloat(currentLng.toFixed(6)),
  //         timestamp: new Date().toISOString()
  //       }

  //       setTrackingPath((prev) => {
  //         const newPath = [...prev, newPoint]

  //         // Use the ref instead of state variable
  //         if (activeTrackIdRef.current) {
  //           syncTrackingPoints(activeTrackIdRef.current, newPoint)
  //         }

  //         return newPath
  //       })

  //       setCurrentLocation(newPoint)

  //       if (map) {
  //         map.panTo(newPoint)
  //       }
  //     } else if (!isTrackingRef.current || isPausedRef.current) {
  //       clearInterval(intervalId)
  //     }
  //   }, 1000)

  //   setDummyIntervalId(intervalId)
  // }

  const startTracking = async () => {
    try {
      let startingLocation = currentLocation

      if (!startingLocation) {
        startingLocation = await getCurrentLocation()
      }

      if (
        !startingLocation ||
        typeof startingLocation.lat !== 'number' ||
        typeof startingLocation.lng !== 'number'
      ) {
        throw new Error('Invalid starting location')
      }

      // Start track session via API
      const trackId = await startTrackSession(startingLocation)

      if (!trackId) {
        toast.error('Failed to start tracking session')
        return // STOP here if API fails
      }

      const initialPath = [startingLocation]
      setTrackingPath(initialPath)
      setCurrentLocation(startingLocation)

      // STORE THE ORIGINAL START LOCATION
      setOriginalStartLocation(startingLocation)

      // SYNC THE STARTING POINT IMMEDIATELY
      if (trackId) {
        syncTrackingPoints(trackId, startingLocation)
      }

      setTrackingPins((prev) => ({
        ...prev,
        startPin: null,
        stopPin: null
      }))

      setIsTracking(true)
      setIsPaused(false)
      isTrackingRef.current = true
      isPausedRef.current = false
      setTrackingTimer(0)

      // START REAL LOCATION TRACKING FOR CONTINUOUS SYNC
      startLocationTracking()

      // REMOVED: simulateDummyTracking(startingLocation)

      // toast.success('Live tracking started from your current location')
    } catch (error) {
      toast.error(
        `Failed to start tracking: ${error.message || 'Unknown error'}`,
        {
          duration: 5000
        }
      )
    }
  }

  const handleAddPin = () => {
    if (!currentLocation) {
      toast.error('No current location available')
      return
    }

    setPinModal({
      latitude: currentLocation.lat,
      longitude: currentLocation.lng
    })

    setTrackingSidebarActive(false)
  }

  // In the main component, update the handlePinCreated function:

  const handlePinCreated = async (pinData) => {
    try {
      // Extract ALL image URLs for photos array
      const imageUrls =
        pinData.images && pinData.images.length > 0
          ? pinData.images.map((img) => ({
            url: img.url,
            thumbnail: img.thumbnail,
            name: img.name,
            fileType: img.fileType || img.type,
            fileSize: img.fileSize || img.size,
            id: img.id
          }))
          : []

      // Use first image as photo_url for backward compatibility
      const photoUrl = imageUrls.length > 0 ? imageUrls[0].url : null

      // Create pin data for API
      // Create pin data for API
      const pinApiData = {
        incident_id: selectedIncident.id,
        track_id: activeTrackId, // optional
        latitude: pinData.latitude,
        longitude: pinData.longitude,
        name: pinData.label,
        description: pinData.description,
        color: `#ff${pinData?.color.replace('#', '')}`,

        // NEW KEY — attachments
        attachments: imageUrls.map((img) => ({
          latitude: pinData.latitude,
          longitude: pinData.longitude,
          photo_url: img.url,
          thumbnail: img.thumbnail,
          fileType: img.fileType,
          fileSize: img.fileSize
        }))
      }

      // Call the API to create pin
      const response = await tracks.createPin(pinApiData)
      const localImages = imageUrls.map((img) => ({
        url: img.url,
        thumbnail: img.thumbnail,
        name: img.name,
        fileType: img.fileType,
        fileSize: img.fileSize,
        id: img.id
      }))

      const customPin = {
        id: response.data.id || Date.now().toString(),
        position: {
          lat: pinData.latitude,
          lng: pinData.longitude
        },
        type: 'custom',
        label: pinData.label,
        description: pinData.description,
        color: pinData.color,
        timestamp: new Date().toISOString(),
        images: localImages, // Store all images
        user_id: user?.id,
        incident_id: selectedIncident.id,
        track_id: activeTrackId
      }

      setTrackingPins((prev) => {
        const currentCustomPins = Array.isArray(prev.customPins)
          ? prev.customPins
          : []
        return {
          ...prev,
          customPins: [...currentCustomPins, customPin]
        }
      })

      // EMIT SOCKET EVENT FOR REAL-TIME PIN UPDATE
      // if (socket && selectedIncident?.id) {
      //   socket.emit('add_pin_track', {
      //     incidentId: selectedIncident.id,
      //     userId: user?.id,
      //     track: activeTrackId,
      //     pin: {
      //       id: response.data.id,
      //       incident_id: selectedIncident.id,
      //       user_id: user?.id,
      //       track_id: activeTrackId,
      //       latitude: pinData.latitude,
      //       longitude: pinData.longitude,
      //       name: pinData.label,
      //       description: pinData.description,
      //       photo_url: photoUrl, // For backward compatibility
      //       photos: imageUrls.map((img) => img.url), // Array of all images
      //       created_at: new Date().toISOString(),
      //       updated_at: new Date().toISOString()
      //     }
      //   })
      // }

      if (selectedIncident?.chatId) {
        try {
          const PHOTO_LABEL = '📷 Photo added to pin'
          // Create message object for chat - include all images
          const messageData = createPinMessage(
            pinData,
            user,
            selectedIncident,
            localImages, // Pass all images instead of just first
            response.data.id,
            activeTrackId,
            PHOTO_LABEL
          )
          await addMessageToChat(selectedIncident.chatId, messageData)
        } catch (chatError) {
          toast.error('Pin created but failed to add to chat')
        }
      } else {
        console.log('No chatId found for incident:', selectedIncident.id)
      }

      setPinModal(null)
      setTrackingSidebarActive(false)
    } catch (error) {
      console.error('Error creating pin:', error)
      toast.error('Failed to create pin')
    }
  }

  const cancelTracking = async () => {
    try {
      if (locationWatchId) {
        navigator.geolocation.clearWatch(locationWatchId)
        setLocationWatchId(null)
      }

      // Store trackId before clearing state
      const trackIdToRemove = activeTrackId

      // API call to cancel the track session - wait for success
      if (trackIdToRemove) {
        await tracks.cancelTrack(trackIdToRemove)

        // Only proceed with UI updates if API succeeded
        // EMIT SOCKET EVENT FOR REAL-TIME TRACK REMOVAL with proper structure
        if (socket && selectedIncident?.id) {
          socket.emit('remove_track', {
            trackId: trackIdToRemove,
            incidentId: selectedIncident.id,
            userId: user?.id,
            timestamp: new Date().toISOString()
          })

          // Also emit pin removal for all pins associated with this track
          const trackPins = trackingPins.customPins.filter(
            (pin) => pin.track_id === trackIdToRemove
          )

          // Emit removal for each pin
          trackPins.forEach((pin) => {
            socket.emit('remove_pin_track', {
              pinId: pin.id,
              incidentId: selectedIncident.id,
              userId: user?.id,
              timestamp: new Date().toISOString()
            })
          })
        }

        // Remove from local tracksList immediately
        setTracksList((prev) =>
          prev.filter((track) => track.id !== trackIdToRemove)
        )

        // Remove pins associated with this track from local state
        setTrackingPins((prev) => ({
          ...prev,
          customPins: prev.customPins.filter(
            (pin) => pin.track_id !== trackIdToRemove
          )
        }))
      }

      // CLEAR ONLY CURRENT USER'S TRACKING DATA
      setTrackingPath([])
      setOriginalStartLocation(null)
      setActiveTrackId(null)
      setIsTracking(false)
      setIsPaused(false)
      setTrackingTimer(0)

      // Update refs
      isTrackingRef.current = false
      isPausedRef.current = false
      activeTrackIdRef.current = null

      toast.success('Tracking cancelled successfully')
    } catch (error) {
      toast.error('Error cancelling tracking session')
      // Don't clear states if API failed
    }
  }

  const pauseTracking = async () => {
    if (!activeTrackId) return

    const success = await pauseTrackSession() // wait for API

    if (success) {
      setIsPaused(true)
      isPausedRef.current = true
    } else {
      toast.error('Failed to pause tracking. Tracking is still active.')
    }
  }

  // const resumeTracking = async () => {
  //   if (activeTrackId) {
  //     await resumeTrackSession()
  //   }

  //   if (currentLocation && map) {
  //     map.panTo(currentLocation)
  //   }

  //   if (trackingPath.length > 0) {
  //     const lastPoint = trackingPath[trackingPath.length - 1]
  //     simulateDummyTracking(lastPoint)
  //   } else if (currentLocation) {
  //     simulateDummyTracking(currentLocation)
  //   }

  //   setIsPaused(false)
  //   isPausedRef.current = false
  // }

  const resumeTracking = async () => {
    if (!activeTrackId) return

    const success = await resumeTrackSession() // wait for API

    if (success) {
      if (currentLocation && map) {
        map.panTo(currentLocation)
      }

      setIsPaused(false)
      isPausedRef.current = false
    } else {
      toast.error('Failed to resume tracking. Tracking is still paused.')
    }
  }

  const stopTracking = async () => {
    // Open the modal instead of directly stopping
    setStopTrackModal(true)
    setTrackingSidebarActive(false) // Close sidebar if open
  }

  // Create a new function to actually end the track with name
  const endTrackWithName = async (trackName) => {
    try {
      if (locationWatchId) {
        navigator.geolocation.clearWatch(locationWatchId)
        setLocationWatchId(null)
      }

      // End track session via API - wait for success
      if (activeTrackId) {
        const success = await endTrackSession(trackName)

        if (!success) {
          toast.error('Failed to stop tracking. Tracking is still active.')
          return // don't clear UI/state
        }

        // Only proceed if API succeeded
        if (selectedIncident?.id) {
          loadIncidentTracks(selectedIncident.id)
        }
      }

      // Clear states ONLY if API succeeded
      setIsTracking(false)
      setIsPaused(false)
      isTrackingRef.current = false
      isPausedRef.current = false
      setTrackingTimer(0)

      // Clear the original start location only for current user
      setOriginalStartLocation(null)

      setActiveTrackId(null)
      activeTrackIdRef.current = null

      setStopTrackModal(false) // Close modal
      toast.success('Tracking stopped successfully')
    } catch (error) {
      toast.error('Failed to stop tracking')
      // Don't clear states if API failed
    }
  }

  useEffect(() => {
    isTrackingRef.current = isTracking
    isPausedRef.current = isPaused
  }, [isTracking, isPaused])

  const resetTracking = () => {
    // if (dummyIntervalId) {
    //   clearInterval(dummyIntervalId)
    //   setDummyIntervalId(null)
    // }

    if (locationWatchId) {
      navigator.geolocation.clearWatch(locationWatchId)
      setLocationWatchId(null)
    }

    setTrackingPath([])
    setOriginalStartLocation(null) // Clear original start location
    setTrackingPins({
      startPin: null,
      stopPin: null,
      customPins: []
    })
    setActiveTrackId(null)
    activeTrackIdRef.current = null
    setIsTracking(false)
    setIsPaused(false)
    setTrackingTimer(0)

    // Update refs
    isTrackingRef.current = false
    isPausedRef.current = false

    setTrackingSidebarActive(false)
  }

  const handlePolylineClick = () => {
    if (isTracking) {
      // toast.info('A tracking session is currently active')
      return
    }

    setTrackingSidebarActive(true)
  }

  // Add this simple function to the map component
  const zoomToPin = useCallback(
    (lat, lng) => {
      if (map && lat && lng) {
        const location = new window.google.maps.LatLng(lat, lng)
        map.setCenter(location)
        map.setZoom(17)

        // Find and highlight the pin
        const pin = trackingPins.customPins?.find(
          (p) =>
            Math.abs(p.position.lat - lat) < 0.0001 &&
            Math.abs(p.position.lng - lng) < 0.0001
        )

        if (pin) {
          setTimeout(() => {
            // You might need to find the actual marker instance
            const markers = document.querySelectorAll('.custom-pin-marker')
            markers.forEach((marker) => {
              // Add highlight effect
              marker.classList.add('map-highlight')
            })
          }, 100)
        }
      }
    },
    [map, trackingPins.customPins]
  )

  // Make it globally available
  useEffect(() => {
    window.zoomToPin = zoomToPin
    return () => delete window.zoomToPin
  }, [zoomToPin])

  return (
    <GoogleMap
      onLoad={(map) => {
        setMap(map)
        setMapContext(map)
        setTimeout(() => map.panTo(center), 0)
      }}
      onUnmount={() => {
        dragging.current = false
        setMap(null)
      }}
      mapContainerStyle={styles.mapContainer}
      options={mapOptions}
      center={cachedCenter.current}
      zoom={zoom}
      onClick={onClick}
      onZoomChanged={() => map && setMapZoom(map.zoom)}
      onCenterChanged={() =>
        map &&
        map.center &&
        setMapCenter({ lng: map.center.lng(), lat: map.center.lat() })
      }
      onDragStart={() => (dragging.current = true)}
      onDragEnd={() => (dragging.current = false)}>
      <AddressSearchBox
        addressSearchBoxRef={addressSearchBoxRef}
        leftMargin={leftMargin}
        mapSettings={mapSettings}
        setMapCenter={setMapCenter}
        onMapClick={onMapClick}
      />
      {selectedIncident && (
        <div className='selected-incident' style={{ left: `${leftMargin}px` }}>
          Selected incident: {selectedIncident.description}
        </div>
      )}
      <DeviceMarkersWrapper
        deviceLocations={deviceLocations}
        openSecondaryPanel={openSecondaryPanel}
        iconSize={mapSettings.iconSize || 5}
      />
      <IncidentMarkersWrapper
        incidents={incidents}
        selectedIncident={selectedIncident}
        onClick={onIncidentMarkerClick}
        iconSize={mapSettings.iconSize || 5}
      />
      <IncidentMapMarkersWrapper
        incidents={incidents}
        selectedIncident={selectedIncident}
        onClick={onIncidentMapMarkerClick}
        iconSize={mapSettings.iconSize || 5}
      />
      {selectedIncident && createPin && createPin.granted && (
        <DraggableMapMarker
          map={map}
          selectedIncident={selectedIncident}
          setMapMarkerModal={setMapMarkerModal}
        />
      )}
      {selectedIncident && (
        <ModalDialog
          active={mapMarkerModal}
          onClose={() => setMapMarkerModal()}>
          {mapMarkerModal && (
            <CreateMapMarkerModal
              incident={selectedIncident}
              latitude={mapMarkerModal.latitude}
              longitude={mapMarkerModal.longitude}
              onMarkerCreated={() => setMapMarkerModal()}
              user={user}
            />
          )}
        </ModalDialog>
      )}
      {mapSelection && <Marker position={convertLocation(mapSelection.loc)} />}
      {mapSettings.showTrafficLayer && <TrafficLayer />}
      {infoWindowConfig && (
        <TrackInfoWindow
          track={infoWindowConfig.track}
          anchor={infoWindowConfig.anchor}
          onClose={infoWindowConfig.onClose}
          tracks={tracks}
          currentUserId={user?.id}
          onDeleteTrack={(trackId) => {
            setTracksList((prev) => prev.filter((t) => t.id !== trackId))
            setLiveTrackUpdates((prev) => {
              const newUpdates = { ...prev }
              delete newUpdates[trackId]
              return newUpdates
            })
          }}
          liveTrackData={
            infoWindowConfig.track
              ? liveTrackUpdates[infoWindowConfig.track.id]
              : null
          }
        />
      )}
      {customPinInfo && (
        <CustomPinInfoWindow
          pin={customPinInfo.pin}
          anchor={customPinInfo.anchor}
          onClose={() => setCustomPinInfo(null)}
          tracks={tracks}
          setTrackingPins={setTrackingPins}
          currentUserId={user?.id}
          socket={socket} // Add this line
        />
      )}
      {infoBox}
      {tracksList
        .filter((track) => {
          if (showOtherUsers) {
            // Show all tracks when toggle is ON
            return true
          } else {
            // Show only my tracks when toggle is OFF
            return track.user_id === user?.id
          }
        })
        .map((track) => (
          <React.Fragment key={track.id}>
            <TrackPolyline
              track={track}
              onClick={onTrackClick}
              isSelected={selectedTrack?.id === track.id}
            />
            <TrackMarkers
              track={track}
              isSelected={selectedTrack?.id === track.id}
            />
          </React.Fragment>
        ))}
      {/* {selectedIncident && (
        <TrackingControl
          onOpenTracking={() => setTrackingSidebarActive(true)}
          isTrackingActive={isTracking}
          currentLocation={currentLocation}
          trackingPath={trackingPath}
        />
      )} */}
      {Array.isArray(trackingPins?.customPins) &&
        trackingPins?.customPins?.map((pin) => (
          <CustomPin
            key={pin.id}
            pin={pin}
            map={map}
            onClick={onCustomPinClick}
          />
        ))}
      {/* Current Tracking Path */}

      {isTracking && trackingPath.length > 1 && (
        <SmoothedLivePolyline
          path={trackingPath}
          onClick={handlePolylineClick}
        />
      )}

      {/* {isTracking &&
        trackingPath.length > 1 &&
        (() => {
          const smoothedCurrentPath = React.useMemo(() => {
            const gpsCleaner = new GPSWanderingDetector()
            const cleaned = gpsCleaner.removeWanderingPoints({
              coordinates: trackingPath
            })
            return cleaned
          }, [trackingPath])

          return (
            <Polyline
              path={smoothedCurrentPath}
              options={{
                strokeColor: '#005ad0',
                strokeOpacity: 0.8,
                strokeWeight: 6,
                clickable: true,
                draggable: false,
                editable: false,
                visible: true,
                zIndex: 1
              }}
              onClick={handlePolylineClick}
            />
          )
        })()} */}
      {/* {currentLocation && isValidLocation(currentLocation) && (
        <Marker
          position={currentLocation}
          icon={{
            url:
              'data:image/svg+xml;base64,' +
              btoa(`
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="10" fill="${currentLocation.source === 'ip' ? '#ff9900' : '#3366ff'
                }" stroke="white" stroke-width="2"/>
            <circle cx="16" cy="16" r="4" fill="white"/>
            ${currentLocation.source === 'ip'
                  ? '<text x="16" y="30" text-anchor="middle" fill="white" font-size="8" font-weight="bold">IP</text>'
                  : ''
                }
          </svg>
        `),
            scaledSize: new window.google.maps.Size(32, 32),
            anchor: new window.google.maps.Point(16, 16)
          }}
          title={
            currentLocation.source === 'ip'
              ? `Approximate location (${currentLocation.city || 'Unknown'})`
              : isTracking
                ? 'Your current location (Tracking)'
                : 'Your current location'
          }
          zIndex={1000}
        />
      )} */}
      {/* NEW: Pin Modal */}
      {pinModal && (
        <ModalDialog
          active={true}
          onClose={() => setPinModal(null)}
          title='Add Pin to Tracking Path'>
          <TrackingPinModal
            latitude={pinModal.latitude}
            longitude={pinModal.longitude}
            onMarkerCreated={handlePinCreated}
            activeTrackId={activeTrackId} // Add this line
          />
        </ModalDialog>
      )}
      <TrackingSidebar
        active={trackingSidebarActive}
        onClose={() => setTrackingSidebarActive(false)}
        isTracking={isTracking}
        setIsTracking={setIsTracking}
        showOtherUsers={showOtherUsers}
        setShowOtherUsers={setShowOtherUsers}
        timer={trackingTimer}
        isPaused={isPaused}
        setIsPaused={setIsPaused}
        startTracking={startTracking}
        pauseTracking={pauseTracking}
        resumeTracking={resumeTracking}
        stopTracking={stopTracking}
        trackingPath={trackingPath}
        currentLocation={currentLocation}
        trackingPins={trackingPins}
        onResetTracking={resetTracking}
        onAddPin={handleAddPin}
        onCancelTracking={cancelTracking}
        activeTrackId={activeTrackId}
        hasActiveTrack={isTracking}
      />

      {/* Stop Tracking Modal */}
      {stopTrackModal && (
        <ModalDialog
          active={true}
          onClose={() => setStopTrackModal(false)}
          title='Name Your Track'>
          <div style={{ padding: '20px', minWidth: '300px' }}>
            <p style={{ marginBottom: '20px' }}>
              Please enter a name for your tracking session before stopping:
            </p>

            <div style={{ marginBottom: '20px' }}>
              <input
                type='text'
                id='trackName'
                placeholder='Enter track name'
                defaultValue=''
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px'
              }}>
              <button
                onClick={() => setStopTrackModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}>
                Cancel
              </button>

              <button
                onClick={() => {
                  const trackName = document.getElementById('trackName').value
                  if (trackName.trim()) {
                    endTrackWithName(trackName.trim())
                  } else {
                    toast.error('Please enter a track name')
                  }
                }}
                style={{
                  padding: '10px 20px',
                  background: '#eb144c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}>
                Stop & Save
              </button>
            </div>
          </div>
        </ModalDialog>
      )}
      <DeleteConfirmationModal
        open={deletePinModal.open}
        onClose={() => setDeletePinModal({ open: false, marker: null, incident: null })}
        onConfirm={handleConfirmDeletePin}
        title="Delete Pin"
        message="Are you sure you want to delete this pin?"
      />
    </GoogleMap>
  )
}
